import { Component, ElementRef, Inject, OnDestroy, QueryList, ViewChildren } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
(pdfMake as any).vfs = (pdfFonts as any).pdfMake?.vfs || (pdfFonts as any).vfs;
import htmlToPdfmake from 'html-to-pdfmake';
import { Logo } from '../../common/logo-base64';

@Component({
    selector: 'ai-suggestion-dialog',
    templateUrl: './ai-suggestion-dialog.component.html',
    styleUrl: './ai-suggestion-dialog.component.css',
    standalone: false
})
export class AiSuggestionDialogComponent implements OnDestroy {
  @ViewChildren('chartWrapper', { read: ElementRef }) chartWrappers!: QueryList<ElementRef<HTMLElement>>;

  title: string;
  message: string;
  charts: AiSuggestionDialogChart[];
  onActions?: () => void;
  actionsStatus: AiActionsStatus = 'ready';
  downloading: boolean = false;
  private actionsStatusSubscription?: Subscription;

  constructor(public dialogRef: MatDialogRef<AiSuggestionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AiSuggestionDialogModel) {
    this.title = data.title;
    this.message = data.message;
    this.charts = data.charts ?? [];
    this.onActions = data.onActions;
    this.actionsStatusSubscription = data.actionsStatus$?.subscribe(status => this.actionsStatus = status);
  }

  ngOnDestroy(): void {
    this.actionsStatusSubscription?.unsubscribe();
  }

  onClose(): void {
    this.dialogRef.close();
  }

  actions(): void {
    this.onActions?.();
  }

  downloadAsPDF(): void {
    this.downloading = true;
    try {
      const pdfContent: any[] = [
        { image: Logo.proEconomyLogoBase64Url, width: 120, margin: [0, 0, 0, 12] },
        { text: this.title, style: 'dialogTitle', margin: [0, 0, 0, 16], alignment: 'center' },
        ...this.getChartPdfContent()
      ];

      const htmlContent = htmlToPdfmake(this.message);
      if (Array.isArray(htmlContent)) {
        pdfContent.push(...htmlContent);
      } else {
        pdfContent.push(htmlContent);
      }

      const docDefinition: any = {
        content: pdfContent,
        defaultStyle: { fontSize: 11 },
        styles: {
          dialogTitle: {
            fontSize: 16,
            bold: true
          }
        }
      };
      const safeTitle = this.title.replace(/[^a-z0-9 _-]/gi, '_');
      pdfMake.createPdf(docDefinition).download(`${safeTitle}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      this.downloading = false;
    }
  }

  private getChartPdfContent(): any[] {
    if (!this.chartWrappers || this.chartWrappers.length === 0) {
      return [];
    }

    return this.chartWrappers
      .map(wrapper => this.buildChartSvgContent(wrapper.nativeElement))
      .filter((content): content is any => !!content);
  }

  private buildChartSvgContent(wrapper: HTMLElement): any | null {
    const svgElement = wrapper.querySelector('svg');
    if (!svgElement) {
      return null;
    }

    const serializedSvg = new XMLSerializer().serializeToString(svgElement);
    const width = this.parseDimension(svgElement.getAttribute('width')) ?? wrapper.clientWidth ?? 700;
    const height = this.parseDimension(svgElement.getAttribute('height')) ?? wrapper.clientHeight ?? 360;

    return {
      svg: serializedSvg,
      fit: [520, 320],
      margin: [0, 0, 0, 16],
      width,
      height
    };
  }

  private parseDimension(value: string | null): number | null {
    if (!value) {
      return null;
    }

    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
}

export interface AiSuggestionDialogChart {
  options: any;
  height?: string;
  width?: '50%' | '100%';
}

export type AiActionsStatus = 'loading' | 'ready' | 'error';

export class AiSuggestionDialogModel {
  constructor(public title: string, public message: string, public charts?: AiSuggestionDialogChart[],
    public onActions?: () => void, public actionsStatus$?: Observable<AiActionsStatus>) {}
}
